import React, {
  useState,
  useEffect,
  useRef,
  createRef,
  useCallback,
  useMemo,
  useReducer,
} from "react";
import {
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  useMediaQuery,
  TextField,
  FormControlLabel,
  Switch,
  Box,
} from "@mui/material";

import { Chess } from "chess.js";
import { Chessboard } from "react-chessboard";
import { useTranslation } from "react-i18next";
import { useScreenshot, createFileName } from "use-react-screenshot";
import { useParams } from "react-router-dom";
import MoveLog from "./MoveLog";
import ChessButton from "./ChessButton";
import Swal from "sweetalert2";
import EvaluationBar from "./EvaluationBar";
import { useNavigate } from "react-router-dom";
import { sendMessage } from "../services/websocketManager";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import EnhancedChessDrawer from './EnhancedChessDrawer';
// Import icons
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import SettingsIcon from "@mui/icons-material/Settings";
import TourIcon from "@mui/icons-material/Tour";

// Import the fixed tour hook
import { useChessLearnTour } from "./chessLearnTour";

// Initial state for reducer
const initialState = {
  positionDrawerOpen: false,
  fen: "",
  playerColor: "white",
  aiDialogOpen: false,
  fenDialogOpen: false,
  gameOver: false,
  capturedWhite: [],
  capturedBlack: [],
  aiLevel: 3,
  autoAIMoves: false,
  bestMoveGeneral: "",
  showGameHistory: false,
  isCalculating: false,
  customFen: "",
};

// Reducer for game state management
function gameReducer(state, action) {
  switch (action.type) {
    case "TOGGLE_POSITION_DRAWER":
      return { ...state, positionDrawerOpen: action.payload };
    case "SET_FEN":
      return { ...state, fen: action.payload };
    case "SET_PLAYER_COLOR":
      return { ...state, playerColor: action.payload };
    case "TOGGLE_AI_DIALOG":
      return { ...state, aiDialogOpen: action.payload };
    case "TOGGLE_FEN_DIALOG":
      return { ...state, fenDialogOpen: action.payload };
    case "SET_CUSTOM_FEN":
      return { ...state, customFen: action.payload };
    case "SET_GAME_OVER":
      return { ...state, gameOver: action.payload };
    case "SET_CAPTURED_PIECES":
      return {
        ...state,
        capturedWhite: action.payload.white,
        capturedBlack: action.payload.black,
      };
    case "SET_AI_LEVEL":
      localStorage.setItem("aiLevel", action.payload);
      return { ...state, aiLevel: action.payload };
    case "TOGGLE_AUTO_AI":
      const newAuto = !state.autoAIMoves;
      localStorage.setItem("autoAIMoves", JSON.stringify(newAuto));
      return { ...state, autoAIMoves: newAuto };
    case "SET_AUTO_AI":
      localStorage.setItem("autoAIMoves", JSON.stringify(action.payload));
      return { ...state, autoAIMoves: action.payload };
    case "SET_BEST_MOVE":
      return { ...state, bestMoveGeneral: action.payload };
    case "TOGGLE_GAME_HISTORY":
      return { ...state, showGameHistory: action.payload };
    case "SET_CALCULATING":
      return { ...state, isCalculating: action.payload };
    case "RESET_GAME":
      return {
        ...state,
        fen: action?.payload?.fen,
        gameOver: false,
        capturedWhite: [],
        capturedBlack: [],
        bestMoveGeneral: "",
      };
    default:
      return state;
  }
}

// Learn mode configuration - optimized for learning
const learnModeConfig = {
  // Primary Actions
  switchColor: true,
  aiSettings: true,
  
  // Game Actions (focus on essential learning features)
  offerDraw: false,
  saveGame: false,
  inviteViewer: false,
  shareScreen: true, // Keep for sharing positions
  showHistory: true, // Essential for learning
  sendSmile: false,
  
  // Analysis - essential for learning
  gameAnalysis: true,
  
  // Position Controls - important for learning
  setPosition: true,
  copyPgn: true,
  copyFen: true,
  
  // System
  gameInfo: true,
  settings: false,
  account: false,
  
  // Actions
  restartGame: true,
  surrender: false,
  mainMenu: true,
  
  // Blind Chess Features (disabled for learn mode)
  bestMove: false,
  speechSynthesis: false,
  autoAiMove: false,
  hiddenMode: false,
  sayLastMove: false,
  
  // Sections
  showPrimarySection: true,
  showGameActionsSection: true,
  showAnalysisSection: true,
  showPositionSection: true,
  showSystemSection: true,
  showActionsSection: true,
  showBlindSection: false,
};

// Custom hook for responsive design values
const useResponsiveValues = () => {
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  return {
    boardWidth: isMobile ? 350 : 500,
    fontSize: isMobile ? "10px" : "14px",
    buttonWidth: isMobile ? "170px" : "220px",
    buttonHeight: isMobile ? "70px" : "70px",
    capturedPiecesTop: isMobile ? -18 : 1,
    borderWidth: isMobile ? "1px" : "15px",
    marginTop: isMobile ? "45px" : "30px",
    isMobile,
  };
};

// CapturedPieces component
const CapturedPieces = ({ pieces, color, position, isMobile }) => {
  const hasPieces = pieces && pieces.length > 0;

  return (
    <div
      className="captured-pieces-container"
      style={{
        position: "relative",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "5px",
        minHeight: "50px",
      }}
    >
      {hasPieces && (
        <div
          style={{
            display: "flex",
            gap: "3px",
            padding: "6px 10px",
            backgroundColor: color === "b" ? "white" : "white",
            borderRadius: "6px",
            top: position === "top" ? (isMobile ? -20 : 1) : 0,
            position: "relative",
            zIndex: 10,
          }}
        >
          {pieces.map((piece, index) =>
            piece ? (
              <img
                key={index}
                src={`https://res.cloudinary.com/dnlgtek2a/image/upload/v1743416894/Figures/${piece}_${color}.svg`}
                alt={`${color} ${piece}`}
                style={{ width: "20px", height: "20px" }}
              />
            ) : null
          )}
        </div>
      )}
    </div>
  );
};

// Main component
const ResponsiveChessPageLearn = ({
  userData,
  updateBackground,
  setShowAppBar,
}) => {
  const { invitePlayerColor } = useParams();
  const { t } = useTranslation();
  const game = useMemo(() => new Chess(), []);
  const [evaluation, setEvaluation] = useState(null);
  const [gameState, dispatch] = useReducer(gameReducer, {
    ...initialState,
    fen: game?.fen(),
    customFen: game?.fen(),
  });

  const {
    fen,
    playerColor,
    aiDialogOpen,
    fenDialogOpen,
    customFen,
    gameOver,
    capturedWhite,
    capturedBlack,
    aiLevel,
    autoAIMoves,
    bestMoveGeneral,
    showGameHistory,
    isCalculating,
  } = gameState;

  const stockfishRef = useRef(null);
  const responsive = useResponsiveValues();
  const navigate = useNavigate();

  // Tour integration
  const { startChessLearnTour, shouldShowTour, resetTour } = useChessLearnTour();
  const [autoTourTriggered, setAutoTourTriggered] = useState(false);

  // Local storage management
  const [moveHistory, setMoveHistoryState] = useState([
    { fen: game?.fen(), move: null },
  ]);
  const [currentMoveIndex, setCurrentMoveIndexState] = useState(0);

  const ref = createRef(null);
  const [, takeScreenShot] = useScreenshot({
    type: "image/jpeg",
    quality: 1.0,
  });

  // Sleep function for delays
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const historyGame = useMemo(() => new Chess(), []);
  const [checkedKingSquare, setCheckedKingSquare] = useState(null);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);

  const customSquareStyles = checkedKingSquare
    ? {
        [checkedKingSquare]: {
          backgroundColor: "rgba(255, 0, 0, 0.5)",
          borderRadius: "50%",
        },
      }
    : {};

  const highlightStyles = legalMoves.reduce((acc, sq) => {
    const piece = game.get(sq);
    const isCapture = piece && piece.color !== game.turn();

    acc[sq] = {
      backgroundColor: isCapture
        ? "rgba(255, 0, 0, 0.4)" // red for captures
        : "rgba(255, 255, 0, 0.3)", // yellow for regular moves
      borderRadius: "50%",
    };
    return acc;
  }, {});

  // Add selected square styling
  const selectedSquareStyle = selectedSquare ? {
    [selectedSquare]: {
      backgroundColor: "rgba(0, 255, 0, 0.5)", // green for selected piece
      borderRadius: "50%",
    }
  } : {};

  const combinedSquareStyles = {
    ...customSquareStyles,
    ...highlightStyles,
    ...selectedSquareStyle,
  };

  // Tour conditions
  const tourConditions = useMemo(
    () => ({
      canShowTour:
        !gameState.positionDrawerOpen && !aiDialogOpen && !fenDialogOpen,
      shouldShow: shouldShowTour(),
      hasTriggered: autoTourTriggered,
    }),
    [
      gameState.positionDrawerOpen,
      aiDialogOpen,
      fenDialogOpen,
      shouldShowTour,
      autoTourTriggered,
    ]
  );

  // Auto-start tour
  useEffect(() => {
    if (
      tourConditions.canShowTour &&
      tourConditions.shouldShow &&
      !tourConditions.hasTriggered
    ) {
      const timer = setTimeout(() => {
        setAutoTourTriggered(true);
        startChessLearnTour();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [tourConditions, startChessLearnTour]);

  // Reset auto tour trigger when relevant conditions change
  useEffect(() => {
    if (gameState.positionDrawerOpen || aiDialogOpen || fenDialogOpen) {
      setAutoTourTriggered(false);
    }
  }, [gameState.positionDrawerOpen, aiDialogOpen, fenDialogOpen]);

  // Manual tour handler
  const handleManualTour = useCallback(() => {
    setAutoTourTriggered(true);
    startChessLearnTour();
  }, [startChessLearnTour]);

  // Drawer handlers
  const handleOpenDrawer = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    dispatch({ type: "TOGGLE_POSITION_DRAWER", payload: true });
  }, []);

  const handleCloseDrawer = useCallback((e, reason) => {
    if (responsive.isMobile && reason === 'backdropClick') {
      return;
    }
    dispatch({ type: "TOGGLE_POSITION_DRAWER", payload: false });
  }, [responsive.isMobile]);

  // Screenshot functionality
  const download = (
    image,
    { name = "chess-position", extension = "jpg" } = {}
  ) => {
    const a = document.createElement("a");
    a.href = image;
    a.download = createFileName(extension, name);
    a.click();
  };

  const downloadScreenshot = useCallback(() => {
    takeScreenShot(ref.current).then(download);
  }, [takeScreenShot, ref]);

  // Helper function to check if analytics should be shown
  const shouldShowAnalytics = () => {
    const history = getMoveHistory();
    const moveCount = history.filter((entry) => entry.move !== null).length;
    return moveCount >= 5;
  };

  const getKingSquare = (color) => {
    const board = game.board();
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        if (piece && piece.type === "k" && piece.color === color[0]) {
          const fileLetter = "abcdefgh"[file];
          const rankNumber = 8 - rank;
          return `${fileLetter}${rankNumber}`;
        }
      }
    }
    return null;
  };

  // Initialize stockfish and set up background
  useEffect(() => {
    try {
      setShowAppBar(responsive.isMobile ? false : true);
      const isLocalhost = window.location.hostname === "localhost";

      const storedLevel = parseInt(localStorage.getItem("aiLevel"), 10);
      if (!isNaN(storedLevel)) {
        dispatch({ type: "SET_AI_LEVEL", payload: storedLevel });
      }

      const storedAutoAI = localStorage.getItem("autoAIMoves");
      if (storedAutoAI !== null) {
        dispatch({ type: "SET_AUTO_AI", payload: storedAutoAI === "true" });
      }

      stockfishRef.current = new Worker(
        isLocalhost
          ? `${process.env.PUBLIC_URL}/stockfish.wasm.js`
          : `${process.env.PUBLIC_URL}/stockfish.wasm.js`
      );

      stockfishRef.current.postMessage("uci");
      stockfishRef.current.postMessage("isready");

      stockfishRef.current.onmessage = (event) => {
        const message = event.data;

        if (message.startsWith("info depth") && message.includes("score")) {
          const scoreMatch = message.match(/score (cp|mate) (-?\d+)/);
          if (scoreMatch) {
            const type = scoreMatch[1];
            const value = parseInt(scoreMatch[2], 10);

            const isMyTurn =
              (playerColor === "white" && game?.turn() === "w") ||
              (playerColor === "black" && game?.turn() === "b");

            if (isMyTurn) {
              setEvaluation({ type, value });
            }
          }
        }

        if (message.startsWith("bestmove")) {
          const aiMove = message?.split(" ")[1];
          dispatch({ type: "SET_BEST_MOVE", payload: aiMove });

          if (autoAIMoves && game?.turn() !== playerColor[0]) {
            executeBestMove(aiMove);
          }
        }
      };

      if (invitePlayerColor) {
        dispatch({ type: "SET_PLAYER_COLOR", payload: invitePlayerColor });
      }
    } catch (error) {
      console.error(error);
    }
    return () => {
      if (stockfishRef.current) stockfishRef.current.terminate();
    };
  }, [
    autoAIMoves,
    playerColor,
    invitePlayerColor,
    updateBackground,
    setShowAppBar,
    game,
    responsive.isMobile,
  ]);

  // Initialize move history from localStorage
  useEffect(() => {
    if (localStorage.getItem("moveHistory")) {
      setMoveHistory([{ fen: game?.fen(), move: null }]);
    }
    if (localStorage.getItem("currentMoveIndex")) {
      setCurrentMoveIndex(0);
    }

    const history = getMoveHistory();
    const index = getCurrentMoveIndex();

    const savedFEN = localStorage.getItem("learn_chess_fen");
    const fenToLoad = savedFEN || history[index]?.fen;

    if (fenToLoad) {
      try {
        game.load(fenToLoad);
        dispatch({ type: "SET_FEN", payload: fenToLoad });
        updateCapturedPieces();
        localStorage.removeItem("learn_chess_fen");
      } catch (e) {
        console.error("Failed to load FEN:", fenToLoad, e);
      }
    } else {
      dispatch({ type: "SET_FEN", payload: history[index]?.fen });
      game?.load(history[index]?.fen);
      updateCapturedPieces();
    }
  }, [game]);

  // Local storage helper functions
  const getMoveHistory = useCallback(() => {
    return JSON.parse(localStorage.getItem("moveHistory")) || moveHistory;
  }, [moveHistory]);

  const setMoveHistory = (history) => {
    localStorage.setItem("moveHistory", JSON.stringify(history));
    setMoveHistoryState(history);
  };

  const getCurrentMoveIndex = useCallback(() => {
    return (
      parseInt(localStorage.getItem("currentMoveIndex"), 10) || currentMoveIndex
    );
  }, [currentMoveIndex]);

  const setCurrentMoveIndex = (index) => {
    localStorage.setItem("currentMoveIndex", index);
    setCurrentMoveIndexState(index);
  };

  // Calculate captured pieces
  const updateCapturedPieces = useCallback(() => {
    const initialCounts = {
      w: { p: 8, r: 2, n: 2, b: 2, q: 1, k: 1 },
      b: { p: 8, r: 2, n: 2, b: 2, q: 1, k: 1 },
    };

    const currentCounts = {
      w: { p: 0, r: 0, n: 0, b: 0, q: 0, k: 0 },
      b: { p: 0, r: 0, n: 0, b: 0, q: 0, k: 0 },
    };

    game
      ?.board()
      .flat()
      .forEach((square) => {
        if (square) {
          currentCounts[square.color][square.type]++;
        }
      });

    const newWhiteCaptured = [];
    const newBlackCaptured = [];

    const pieceImportance = { k: 1, q: 2, r: 3, b: 4, n: 5, p: 6 };

    Object.keys(initialCounts.w).forEach((piece) => {
      const missingWhitePieces =
        initialCounts.w[piece] - currentCounts.w[piece];
      const missingBlackPieces =
        initialCounts.b[piece] - currentCounts.b[piece];

      if (missingWhitePieces > 0)
        newBlackCaptured.push(...Array(missingWhitePieces).fill(piece));
      if (missingBlackPieces > 0)
        newWhiteCaptured.push(...Array(missingBlackPieces).fill(piece));
    });

    const sortedWhiteCaptured = newWhiteCaptured.sort(
      (a, b) => pieceImportance[a] - pieceImportance[b]
    );
    const sortedBlackCaptured = newBlackCaptured.sort(
      (a, b) => pieceImportance[a] - pieceImportance[b]
    );

    dispatch({
      type: "SET_CAPTURED_PIECES",
      payload: {
        white: sortedWhiteCaptured,
        black: sortedBlackCaptured,
      },
    });
  }, [game]);

  // Stockfish analysis
  const analyzeBestMoves = useCallback(() => {
    if (stockfishRef.current) {
      stockfishRef.current.postMessage(`position fen ${game?.fen()}`);
      stockfishRef.current.postMessage(`go depth ${aiLevel}`);
    }
  }, [game, aiLevel]);

  // Game over handler
  const handleGameOver = useCallback(() => {
    dispatch({ type: "SET_GAME_OVER", payload: true });
  }, []);

  // Apply custom FEN
  const applyCustomFen = useCallback(() => {
    try {
      game?.load(customFen);
      historyGame.reset();
      historyGame.load(customFen);
      dispatch({ type: "SET_FEN", payload: game?.fen() });
      setMoveHistory([{ fen: game?.fen(), move: null }]);
      setCurrentMoveIndex(0);

      updateCapturedPieces();
      dispatch({ type: "TOGGLE_FEN_DIALOG", payload: false });

      if (game?.isGameOver()) {
        handleGameOver();
      } else {
        analyzeBestMoves();
      }

      Swal.fire({
        title: t("FEN position applied successfully"),
        icon: "success",
        draggable: true,
        confirmButtonText: t("OK"),
      });
    } catch (error) {
      Swal.fire({
        title: t("Invalid FEN position"),
        icon: "error",
        draggable: true,
        confirmButtonText: t("OK"),
      });
    }
  }, [
    customFen,
    game,
    historyGame,
    updateCapturedPieces,
    handleGameOver,
    analyzeBestMoves,
    t,
  ]);

  const addToGameHistory = (fen, moveUci, color) => {
    try {
      const moveObj = {
        from: moveUci.substring(0, 2),
        to: moveUci.substring(2, 4),
        promotion: moveUci.length > 4 ? moveUci[4] : undefined,
      };

      const result = historyGame.move(moveObj);
      if (!result) {
        console.error(
          "Invalid move for historyGame",
          moveObj,
          historyGame.fen()
        );
        return;
      }

      const history = getMoveHistory().slice(0, getCurrentMoveIndex() + 1);
      const newEntry = {
        fen,
        move: moveUci.substring(0, 2) + "-" + moveUci.substring(2, 4),
        san: historyGame.history({ verbose: true }).slice(-1)[0].san,
        color,
      };

      const newHistory = [...history, newEntry];
      updateMoveHistory(newHistory);
      setCurrentMoveIndex(newHistory.length - 1);
    } catch (error) {
      console.error("Error adding to game history:", error);
    }
  };

  // Handle chess move
  const handleMove = useCallback(
    (sourceSquare, targetSquare) => {
      let move;
      try {
        move = game.move({
          from: sourceSquare,
          to: targetSquare,
          promotion: "q",
        });

        if (!move) return false;
      } catch (error) {
        return false;
      }

      dispatch({ type: "SET_FEN", payload: game?.fen() });
      updateCapturedPieces();

      if (game.inCheck()) {
        const colorInCheck = game.turn();
        const kingSquare = getKingSquare(
          colorInCheck === "w" ? "white" : "black"
        );
        setCheckedKingSquare(kingSquare);
      } else {
        setCheckedKingSquare(null);
      }

      if (game.isGameOver()) {
        handleGameOver();
      } else if (stockfishRef.current) {
        analyzeBestMoves();
      }

      addToGameHistory(game?.fen(), move.from + move.to, move.color);

      return true;
    },
    [
      game,
      updateCapturedPieces,
      getKingSquare,
      handleGameOver,
      analyzeBestMoves,
      addToGameHistory,
    ]
  );

  const handleSquareClick = useCallback(
    (square) => {
      if (
        (playerColor === "white" && game.turn() !== "w") ||
        (playerColor === "black" && game.turn() !== "b") ||
        gameOver
      ) {
        //return;
      }

      if (selectedSquare === square) {
        setSelectedSquare(null);
        setLegalMoves([]);
        return;
      }

      if (!selectedSquare) {
        const moves = game.moves({ square, verbose: true });
        if (moves.length === 0) return;

        const legalDestinations = moves.map((m) => m.to);
        setSelectedSquare(square);
        setLegalMoves(legalDestinations);
        return;
      }

      const moveResult = handleMove(selectedSquare, square);
      if (moveResult) {
        setSelectedSquare(null);
        setLegalMoves([]);
      } else {
        const moves = game.moves({ square, verbose: true });
        if (moves.length > 0) {
          const legalDestinations = moves.map((m) => m.to);
          setSelectedSquare(square);
          setLegalMoves(legalDestinations);
        } else {
          setSelectedSquare(null);
          setLegalMoves([]);
        }
      }
    },
    [
      playerColor,
      game,
      gameOver,
      selectedSquare,
      setSelectedSquare,
      setLegalMoves,
      handleMove,
    ]
  );

  // Execute best move
  const executeBestMove = useCallback(
    async (aiMove) => {
      dispatch({ type: "SET_CALCULATING", payload: true });

      try {
        await sleep(1000);

        let moveToExecute = aiMove || bestMoveGeneral;

        if (!moveToExecute) {
          const index = getCurrentMoveIndex();
          if (index === 0) {
            moveToExecute = "e2e4";
          }
        }

        if (moveToExecute) {
          const sourceSquare = moveToExecute?.substring(0, 2);
          const targetSquare = moveToExecute?.substring(2, 4);
          const result = handleMove(sourceSquare, targetSquare);
          if (result) {
            addToGameHistory(
              game?.fen(),
              moveToExecute,
              game.turn() === "w" ? "b" : "w"
            );
          }
        }
      } catch (error) {
        console.error("Error executing best move:", error);

        Swal.fire({
          title: t("Error"),
          text: t("Error executing move"),
          icon: "error",
          draggable: true,
          confirmButtonText: t("OK"),
        });
      } finally {
        dispatch({ type: "SET_CALCULATING", payload: false });
      }
    },
    [
      addToGameHistory,
      bestMoveGeneral,
      game,
      getCurrentMoveIndex,
      handleMove,
      t,
    ]
  );

  // Check game status
  const checkGameStatus = useCallback(() => {
    let status = "in_progress";

    if (game.isCheckmate()) {
      status =
        game.turn() === "w"
          ? "black_won_by_mat_white"
          : "white_won_by_mat_black";
    } else if (game.isStalemate()) {
      status = "stalemate";
    } else if (game.isThreefoldRepetition()) {
      status = "threefold_repetition";
    } else if (game.isInsufficientMaterial()) {
      status = "insufficient_material";
    } else if (game.isCheck()) {
      status = "check";
    } else if (game.isDraw()) {
      status = "draw";
    }

    return status;
  }, [game]);

  function handleResetGame() {
    game.reset();
    historyGame.reset();

    localStorage.removeItem("learn_chess_fen");
    setMoveHistory([{ fen: game.fen(), move: null }]);
    setCurrentMoveIndex(0);
    localStorage.removeItem("moveHistory");
    localStorage.removeItem("currentMoveIndex");

    dispatch({
      type: "RESET_GAME",
      payload: { fen: game.fen() },
    });
  }

  const togglePlayerColor = useCallback(() => {
    const newColor = playerColor === "white" ? "black" : "white";
    dispatch({ type: "SET_PLAYER_COLOR", payload: newColor });

    analyzeBestMoves();

    if (newColor !== playerColor) {
      executeBestMove();
    }
  }, [playerColor, dispatch, analyzeBestMoves, executeBestMove]);

  const updateMoveHistory = (history) => {
    let moveNumber = 1;

    const enriched = history.map((entry, index) => {
      const updated = { ...entry };
      if (entry.color === "white") {
        updated.moveNumber = moveNumber;
      } else if (entry.color === "black") {
        updated.moveNumber = moveNumber;
        moveNumber++;
      }
      return updated;
    });

    localStorage.setItem("moveHistory", JSON.stringify(enriched));
  };

  function sendToGameAnalysis() {
    localStorage.setItem("learn_chess_fen", game.fen());
    const pgn = game.pgn();
    const generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "analitics_request";
    generalMessage.gameInfo.gameId = crypto.randomUUID();
    generalMessage.gameInfo.basicGameHistory =
      localStorage.getItem("moveHistory");
    generalMessage.gameInfo.color = playerColor;
    generalMessage.gameInfo.pgn = pgn;
    generalMessage.gameInfo.gameTeamType = "skill_building";
    sendMessage(generalMessage);
    navigate("/loader", {
      state: {
        boardWidth: 400,
        message: "Wait_for_analyzing",
        lottieUrl:
          "https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418492/Lottie/analysis.json",
      },
    });
  }

  // Memoized UI components
  const chessboardComponent = useMemo(
    () => (
      <div ref={ref} className="chess-board-container">
        <div
          style={{
            justifyContent: "center",
            alignItems: "center",
            border: `${responsive.borderWidth} solid #ccc`,
            borderRadius: "5px",
            boxShadow: "0 15px 15px black",
            backgroundColor: "black",
            padding: "5px",
            position: "relative",
            zIndex: 10,
            marginTop: responsive.isMobile ? "-25px" : "0px",
          }}
        >
          <Chessboard
            position={fen}
            onPieceDrop={handleMove}
            boardOrientation={playerColor}
            boardWidth={responsive.boardWidth}
            ariaLabel={t("Chess board")}
            onSquareClick={handleSquareClick}
            customSquareStyles={combinedSquareStyles}
          />
        </div>
      </div>
    ),
    [
      ref,
      responsive.borderWidth,
      responsive.isMobile,
      responsive.boardWidth,
      fen,
      handleMove,
      playerColor,
      t,
      handleSquareClick,
      combinedSquareStyles,
    ]
  );

  const controlButtons = useMemo(
    () => (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          border: `${responsive.borderWidth}rgb(96, 102, 104)`,
          borderRadius: "15px",
          padding: "4px",
          position: "relative",
          zIndex: 10,
          marginTop: responsive.marginTop,
          gap: "5px",
        }}
      >
        <div data-tour="best-move">
          <ChessButton
            text={t("Best Move")}
            onPress={() => executeBestMove()}
            fontSize={responsive.fontSize}
            width={responsive.buttonWidth}
            height={responsive.buttonHeight}
            disabled={isCalculating}
            icon={
              <EmojiObjectsIcon
                sx={{
                  fontSize: responsive.isMobile ? 16 : 20,
                  marginRight: "5px",
                }}
              />
            }
          />
        </div>

        <div data-tour="extra-controls">
          <ChessButton
            text={t("Extra Controls")}
            onPress={handleOpenDrawer}
            fontSize={responsive.fontSize}
            width={responsive.buttonWidth}
            height={responsive.buttonHeight}
            icon={
              <SettingsIcon
                sx={{
                  fontSize: responsive.isMobile ? 16 : 20,
                  marginRight: "5px",
                }}
              />
            }
          />
        </div>
      </div>
    ),
    [t, executeBestMove, responsive, isCalculating, handleOpenDrawer]
  );

  const aiLevelDialog = useMemo(
    () => (
      <Dialog
        open={aiDialogOpen}
        onClose={() => dispatch({ type: "TOGGLE_AI_DIALOG", payload: false })}
        aria-labelledby="ai-level-dialog-title"
        sx={{
          zIndex: 10000,
        }}
      >
        <DialogTitle id="ai-level-dialog-title">
          {t("Select AI Level")}
        </DialogTitle>
        <DialogContent>
          <Stack
            direction="column"
            spacing={1}
            flexWrap="wrap"
            alignItems="center"
          >
            <FormControlLabel
              control={
                <Switch
                  checked={autoAIMoves}
                  onChange={() => dispatch({ type: "TOGGLE_AUTO_AI" })}
                  name="autoAIMoves"
                  color="primary"
                />
              }
              label={t("Auto AI Move")}
              style={{ marginBottom: "0px" }}
            />

            {[
              { title: t("Beginner Levels"), levels: [1, 2, 3, 4] },
              { title: t("Intermediate Levels"), levels: [5, 6, 7, 8] },
              { title: t("Advanced Levels"), levels: [9, 10, 11, 12] },
              { title: t("Expert Levels"), levels: [13, 14, 15, 16] },
              { title: t("Grandmaster Levels"), levels: [17, 18, 19, 20] },
            ].map((group) => (
              <React.Fragment key={group.title}>
                <Typography variant="h6">{t(group.title)}</Typography>
                <Stack direction="row" spacing={1} justifyContent="center">
                  {group.levels.map((level) => (
                    <Button
                      key={level}
                      variant="outlined"
                      style={{
                        backgroundColor: aiLevel === level ? "#4CAF50" : "",
                      }}
                      onClick={() => {
                        dispatch({ type: "SET_AI_LEVEL", payload: level });
                      }}
                    >
                      {level}
                    </Button>
                  ))}
                </Stack>
              </React.Fragment>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              dispatch({ type: "TOGGLE_AI_DIALOG", payload: false })
            }
          >
            {t("OK")}
          </Button>
        </DialogActions>
      </Dialog>
    ),
    [aiDialogOpen, aiLevel, autoAIMoves, t]
  );

  const fenDialog = useMemo(
    () => (
      <Dialog
        sx={{
          zIndex: 10000,
        }}
        open={fenDialogOpen}
        onClose={() => dispatch({ type: "TOGGLE_FEN_DIALOG", payload: false })}
        aria-labelledby="fen-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="fen-dialog-title">
          {t("Set Starting Position")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: "10px" }}>
            {t(
              "Enter a valid FEN (Forsyth-Edwards Notation) string to set a custom board position"
            )}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="fen"
            label={t("FEN")}
            type="text"
            fullWidth
            variant="outlined"
            value={customFen}
            onChange={(e) =>
              dispatch({ type: "SET_CUSTOM_FEN", payload: e.target.value })
            }
            helperText={t(
              "Example: rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              dispatch({ type: "TOGGLE_FEN_DIALOG", payload: false })
            }
          >
            {t("Cancel")}
          </Button>
          <Button onClick={applyCustomFen} color="primary" variant="contained">
            {t("Apply Position")}
          </Button>
        </DialogActions>
      </Dialog>
    ),
    [fenDialogOpen, customFen, applyCustomFen, t]
  );

  // Render the component
  return (
    <div>
      {/* Tour Control Buttons */}
      {!gameState.positionDrawerOpen && !aiDialogOpen && !fenDialogOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 80,
            right: 20,
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
        </Box>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          margin: "0 auto",
          position: "relative",
          paddingBottom: "20px",
          width: `${responsive.boardWidth}px`,
        }}
      >
        <div
          style={{
            marginTop: responsive.isMobile ? "5px" : "5px",
            marginBottom: responsive.isMobile ? "22px" : "2px",
          }}
        ></div>

        {/* Captured Pieces - Top */}
        {!gameOver && (
          <div>
            {playerColor === "black" ? (
              <CapturedPieces
                pieces={capturedWhite}
                color="b"
                position="top"
                isMobile={responsive.isMobile}
              />
            ) : (
              <CapturedPieces
                pieces={capturedBlack}
                color="w"
                position="top"
                isMobile={responsive.isMobile}
              />
            )}
          </div>
        )}

        <Stack direction="row" spacing={2}>
          {/* Chessboard */}
          {chessboardComponent}

          {/* Game History (Desktop) */}
          {!responsive.isMobile && showGameHistory && (
            <MoveLog
              isMobileView={false}
              t={t}
              getMoveHistory={getMoveHistory}
              getCurrentMoveIndex={getCurrentMoveIndex}
              setCurrentMoveIndex={setCurrentMoveIndex}
              game={game}
              setFen={(newFen) =>
                dispatch({ type: "SET_FEN", payload: newFen })
              }
              updateCapturedPieces={updateCapturedPieces}
              disableClick={false}
              onClose={() =>
                dispatch({ type: "TOGGLE_GAME_HISTORY", payload: false })
              }
            />
          )}
        </Stack>

        <Stack direction="column" spacing={responsive.isMobile ? -2 : 0}>
          {/* Captured Pieces - Bottom */}
          {!gameOver && (
            <div>
              {playerColor === "black" ? (
                <CapturedPieces
                  pieces={capturedBlack}
                  color="w"
                  position="bottom"
                  isMobile={responsive.isMobile}
                />
              ) : (
                <CapturedPieces
                  pieces={capturedWhite}
                  color="b"
                  position="bottom"
                  isMobile={responsive.isMobile}
                />
              )}
            </div>
          )}
          <div
            style={{
              marginTop: responsive.isMobile ? "2px" : "-20px",
              marginBottom: responsive.isMobile ? "-50px" : "2px",
            }}
          ></div>
        </Stack>

        {/* Control Buttons */}
        {controlButtons}

        {/* AI Level Dialog */}
        {aiLevelDialog}

        {/* FEN Configuration Dialog */}
        {fenDialog}

        {/* Enhanced Drawer - replaces ControlPanel completely */}
        <EnhancedChessDrawer
          open={gameState.positionDrawerOpen}
          onClose={handleCloseDrawer}
          responsive={responsive}
          t={t}
          gameState={gameState}
          dispatch={dispatch}
          game={game}
          togglePlayerColor={togglePlayerColor}
          sendToGameAnalysis={sendToGameAnalysis}
          shouldShowAnalytics={shouldShowAnalytics}
          applyCustomFen={applyCustomFen}
          handleResetGame={handleResetGame}
          navigate={navigate}
          // Props for full functionality
          playerColorFromServer={playerColor}
          gameIdFromServer={null}
          onCaptureBoard={downloadScreenshot}
          setIsViewer={false}
          gameOver={gameOver}
          onAccordionChange={(isExpanded) => {}}
          onGameHistoryChange={(isOpen) =>
            dispatch({ type: "TOGGLE_GAME_HISTORY", payload: isOpen })
          }
          getMoveHistory={getMoveHistory}
          aiLevel={aiLevel}
          gameInfo={null}
          winnerColor={null}
          learnMode={true}
          evaluation={evaluation}
          showGameHistory={showGameHistory}
          userData={userData}
          topSubscription={userData?.subscription}
          generalMessageViaState={null}
          location={typeof window !== 'undefined' ? window.location : {}}
          sendMessage={(msg) => console.log('Send message:', msg)}
          generalMessageTemplate={{}}
          setShowHistoryLimitDialog={() => console.log('Show history limit dialog')}
          GENERAL={{ HOST: window.location.origin + '/' }}
          buttonConfig={learnModeConfig}
          drawerOptions={{
            allowDragDrop: true,
            initialPosition: 'right',
            keepBoardVisible: true,
            autoClose: false,
          }}
        />

        {/* Manual Tour Button */}
        {/* <Button
          onClick={handleManualTour}
          startIcon={<TourIcon />}
          variant="outlined"
          size="small"
          sx={{
            marginTop: "10px",
            color: "white",
            borderColor: "rgba(255, 255, 255, 0.5)",
            textTransform: "none",
            fontSize: "0.9rem",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              borderColor: "white",
            },
          }}
        >
          {t("Show Tour")}
        </Button> */}

        {/* Evaluation Bar */}
        {!gameOver && evaluation && (
          <div style={{ marginTop: "10px" }} className="evaluation-bar">
            <EvaluationBar
              evaluation={evaluation}
              playerColor={playerColor}
              boardWidth={window.innerWidth > 600 ? 500 : 350}
              fontColor="white"
            />
          </div>
        )}

        {/* Move Log (Mobile) */}
        {responsive.isMobile && showGameHistory && (
          <div>
            <MoveLog
              isMobileView={true}
              t={t}
              getMoveHistory={getMoveHistory}
              getCurrentMoveIndex={getCurrentMoveIndex}
              setCurrentMoveIndex={setCurrentMoveIndex}
              game={game}
              setFen={(newFen) =>
                dispatch({ type: "SET_FEN", payload: newFen })
              }
              updateCapturedPieces={updateCapturedPieces}
              disableClick={false}
              onClose={() =>
                dispatch({ type: "TOGGLE_GAME_HISTORY", payload: false })
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ResponsiveChessPageLearn;